import { Router } from '@angular/router';
import { BookmarkRecord } from 'fl-bookmarks';
import { createComposablePlayer } from 'fl-player';
import { ComposablePlayer, Player } from 'fl-player-interface';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { ParentalPinComponent } from 'src/app/shared/shared-components/parental-pin/parental-pin.component';
import { DialogService } from '../services/dialog.service';
import { ParentalControlService } from '../services/evergent/parental-control/parental-control.service';
import { SUBTITLE_SIZES, SUBTITLE_THEMES } from './constants/subtitle.constant';
declare let navigator: any;

const navigateToPlayer = async (
  resource: ResourceVm,
  router: Router,
  episodes?: ContainerVm
): Promise<void> => {
  let playerId = resource.uniqueId;
  let contentType = resource.contentType;
  if (
    resource.contentType.toString() === 'webseries' &&
    episodes &&
    episodes.resources
  ) {
    playerId = episodes.resources[0].uniqueId;
    contentType = 'webepisode';
  }
  await router.navigate([`player/${contentType}/${playerId}`], {
    state: {
      type: contentType,
      id: playerId,
    },
  });
};

export const isFreeMovie = (resource: ResourceVm | undefined): boolean => {
  let isFree = false;
  if (
    resource &&
    resource.entitlement &&
    resource.entitlement.length > 0 &&
    resource.entitlement[0]?.subscriptionPackage
  ) {
    resource.entitlement[0].subscriptionPackage.forEach((data) => {
      if (data.includes('free')) {
        isFree = true;
      }
    });
  }
  return isFree;
};

export const showName = (
  resource: ResourceVm | undefined,
  lang: string = 'en'
): string => {
  if (resource?.isSeries) {
    if (resource?.subtitle !== undefined && resource.subtitle !== '') {
      return resource?.subtitle[lang];
    } else {
      return resource?.title[lang] || undefined;
    }
  } else {
    return undefined;
  }
};

export const correctRouting = async (
  isSubscribed: boolean,
  resource: ResourceVm,
  router: Router,
  errorDialog?: () => void,
  episodes?: ContainerVm
): Promise<void> => {
  const isFree = isFreeMovie(resource);
  if (isFree) {
    await navigateToPlayer(resource, router, episodes);
  } else if (isSubscribed) {
    if (localStorage.getItem('subscription') === 'premium') {
      await navigateToPlayer(resource, router, episodes);
    } else {
      errorDialog?.();
    }
  } else if (!isSubscribed && !isFree) {
    errorDialog?.();
  }
};

/**
 * Clean video html element of font size classes and set the selected size class
 *
 * @param sizes string size array
 * @param video
 */
export const changeSubtitlesFontSize = (selectedSize: string): void => {
  const sizes = SUBTITLE_SIZES;
  const subtitleElement: any = document.getElementsByClassName(
    'shaka-text-container'
  );
  const size = `font-${selectedSize}`;
  {
    sizes.forEach((s) => {
      if (subtitleElement) {
        for (let index = 0; index < subtitleElement.length; ++index) {
          subtitleElement[index].classList.remove(`font-${s}`);
        }
      }
    });
    if (subtitleElement) {
      for (let index = 0; index < subtitleElement.length; ++index) {
        subtitleElement[index].classList.add(size);
      }
    }
  }
};

/**
 * Clean video html element of font theme classes and set the selected theme class
 *
 * @param sizes string themes array
 * @param video
 */
export const changeSubtitlesTheme = (selectedTheme: string): void => {
  const themes = SUBTITLE_THEMES;
  const subtitleElement: any = document.getElementsByClassName(
    'shaka-text-container'
  );
  const theme = `theme-${selectedTheme}`;
  {
    themes.forEach((t) => {
      if (subtitleElement) {
        for (let index = 0; index < subtitleElement.length; ++index) {
          subtitleElement[index].classList.remove(`theme-${t}`);
        }
      }
    });
    if (subtitleElement) {
      for (let index = 0; index < subtitleElement.length; ++index) {
        subtitleElement[index].classList.add(`${theme}`);
        subtitleElement[index].style.opacity = '1';
      }
    }
  }
};

export const disableSubtitles = (): void => {
  const subtitleElement: any = document.getElementsByClassName(
    'shaka-text-container'
  );
  if (subtitleElement) {
    for (let index = 0; index < subtitleElement.length; ++index) {
      subtitleElement[index].style.opacity = '0';
    }
  }
};

export const getComposablePlayerInstance = (
  playerInstance: Player
): ComposablePlayer => {
  return createComposablePlayer(playerInstance);
};

/**
 * Check if is live, livechannel o channel content type
 */
export const isLiveContent = (currentResource: ResourceVm): boolean => {
  return (
    currentResource.type === 'liveevent' ||
    currentResource.type === 'livechannel' ||
    currentResource.type === 'channel'
  );
};

/**
 * Check if is live, livechannel o channel metadata type
 */
export const isLiveByMetadata = (metadataType: string): boolean => {
  return (
    metadataType === 'liveevent' ||
    metadataType === 'livechannel' ||
    metadataType === 'channel'
  );
};

/**
 * Check if the bookmark belongs to the resource since
 * the bookmarks are now at series level
 * and must check if the resource matches the episode.
 *
 * @param currentResource
 * @param response
 * @returns
 */
export const isResourceBookmark = (
  currentResource: ResourceVm,
  response: BookmarkRecord
): boolean => {
  return (
    currentResource &&
    response &&
    (currentResource.contentType !== 'webepisode' ||
      (currentResource.contentType === 'webepisode' &&
        currentResource.id === response.episodeId))
  );
};

/**
 * Check if the connection is getting slow acording to navigator info
 *
 * @returns
 */
export const isSlowConnection = (): boolean => {
  return (
    navigator.connection?.downlink <= 0.6 ||
    navigator.connection?.effectiveType === '2g' ||
    navigator.connection?.effectiveType === 'slow-2g'
  );
};

export const directToPlayer = (resource: ResourceVm) =>
  ['trailer', 'shortvideo', 'livechannel', 'liveevent', 'channel'].includes(
    resource.contentType
  );

/**
 * Validate if the resource should show up next
 *
 * @param currentResource
 * @returns
 */
export const canShowNextContents = (currentResource: ResourceVm): boolean => {
  return !directToPlayer(currentResource);
};

export const getFormattedTime = (time: number): string => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  return hours > 0
    ? `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    : `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
};

export const navigateToPlayerWithValidations = async (
  resource: ResourceVm,
  isParentalEnabled: boolean,
  parentalControlDialogService: ParentalControlService,
  router: Router,
  dialogService: DialogService
): Promise<boolean> => {
  try {
    const ableToPlay = isFreeMovie(resource);
    if (ableToPlay === true) {
      isParentalEnabled = true;
      const allowAccess =
        isParentalEnabled === true
          ? await parentalControlDialogService.checkParentalLevels(resource)
          : true;
      if (allowAccess) {
        const { type, id: contentID } = resource;
        router.navigate([`player/${type}/${contentID}`], {
          state: { type, id: contentID },
        });
      } else {
        dialogService.openDialog(ParentalPinComponent);
      }
    }
    return false;
  } catch (e) {
    return false;
  }
};

// Tizen firmware to year mapping
const tizenMap: Record<string, string[]> = {
  'tizen 2.3': ['2015'],
  'tizen 2.4': ['2016'],
  'tizen 3.0': ['2017'],
  'tizen 4.0': ['2018'],
  'tizen 5.0': ['2019'],
  'tizen 5.5': ['2020'],
  'tizen 6.0': ['2021'],
  'tizen 6.5': ['2022'],
  'tizen 7.0': ['2023'],
  'tizen 8.0': ['2024'],
  'tizen 9.0': ['2025'],
};

// LG Chrome version to year mapping
const lgMap: Record<string, string[]> = {
  'chrome/120.': ['2025'],
  'chrome/108.': ['2024'],
  'chrome/94.': ['2023'],
  'chrome/87.': ['2022'],
  'chrome/79.': ['2021'],
  'chrome/68.': ['2020'],
  'chrome/53.': ['2018', '2019'],
  'chrome/38.': ['2016', '2017'],
};

export const checkDeviceBlockList = (
  userAgent: string,
  blockListValue: string,
  logPrefix: string
): boolean => {
  const ua = userAgent.toLowerCase();

  if (!blockListValue || !blockListValue.trim()) {
    // No config set → ads allowed
    return false;
  }

  let platform: 'lg' | 'tizen' | null = null;
  let yearModels: string[] = [];

  // Detect platform & map to years
  if (ua.includes('tizen')) {
    platform = 'tizen';
    // Use Object.entries for better performance than for...in
    for (const [key, years] of Object.entries(tizenMap)) {
      if (ua.includes(key)) {
        yearModels = years;
        break;
      }
    }
  } else if (ua.includes('web0s') || ua.includes('lgwebostv')) {
    platform = 'lg';
    for (const [key, years] of Object.entries(lgMap)) {
      if (ua.includes(key)) {
        yearModels = years;
        break;
      }
    }
  }

  if (!platform) return false;

  // Normalize block list values (allow ':' or '.')
  const blockList = blockListValue
    .toLowerCase()
    .replace(/:/g, '.')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  console.warn(
    `${logPrefix} Platform: ${platform}, Year Models: ${yearModels}, Block List: ${blockList}`
  );

  // Check platform-wide block
  if (blockList.includes(`${platform}.*`)) return true;

  // Check specific year blocks
  return yearModels.some((year) => blockList.includes(`${platform}.${year}`));
};

export const shouldDisableAds = (
  userAgent: string,
  blockListValue: string
): boolean => {
  return checkDeviceBlockList(userAgent, blockListValue, '[Ads]');
};

export const shouldDisableKeyframes = (
  userAgent: string,
  blockListValue: string
): boolean => {
  return checkDeviceBlockList(userAgent, blockListValue, '[Keyframes]');
};

export const isDeviceBelow2020 = (userAgent?: string): boolean => {
  const ua = (userAgent || navigator.userAgent || '').toLowerCase();

  // Check for Samsung Tizen
  if (ua.includes('tizen')) {
    for (const [key, years] of Object.entries(tizenMap)) {
      if (ua.includes(key)) {
        // Check if any year in the mapping is below 2020
        return years.some((year) => parseInt(year, 10) < 2020);
      }
    }
    // If Tizen detected but no mapping found, assume older device (below 2020)
    return true;
  }

  // Check for LG webOS
  if (ua.includes('web0s') || ua.includes('lgwebostv')) {
    for (const [key, years] of Object.entries(lgMap)) {
      if (ua.includes(key)) {
        // Check if any year in the mapping is below 2020
        return years.some((year) => parseInt(year, 10) < 2020);
      }
    }
    // If LG detected but no mapping found, assume older device (below 2020)
    return true;
  }

  // If neither platform detected, return false (assume newer or unknown)
  return false;
};
